/* GASSISA
=========================================================================================
* Descripción: Modal reporte de Traslados por periodo (Historial del día).
*              Fuente única: SQL/temporales de traslados finados por FechaRegistro.
*              - Incluye aplicados TR (y PE ya finados en SQL) del rango.
*              - Detalle agrupado por folio, origen y destino con el mismo
*                formato del reporte de traslado actual.
* Contrato backend (POST TenantMovimientosInventarios/traslados/reporteperiodo):
*   Filtra temporales/SQL de traslados por FechaRegistro. Filtros opcionales
*   almacenorigen/almacendestino y filtrodescripcion con TRIM + UPPER por LIKE.
*   Vive bajo el decorador anterior para no ajustar nginx.
*   Reemplaza al obsoleto GET .../traslados/imprimirhistorialdia (404).
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 25/09/2026 | LIC. José Luis Aragón C.                       | Creación alineada a reportes periodo
*      2       | 05/10/2026 | LIC. José Luis Aragón C.                       | Vista previa en modal (sin popup-blocker);
*              |            |                                  | detecta JSON-error con HTTP 200 y PDF
*              |            |                                  | vacío; Autocomplete permite TODOS y
*              |            |                                  | texto libre (vacío = TODOS al enviar)
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import React, { Fragment, useState, useEffect } from "react";
import { Modal, Box, Typography, Button, Divider, Alert } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useInventarios } from "../../context/Inventarios/InventariosContext";
import { apiReporteTrasladosPeriodo } from "../../settings/ApiConfig";

const modalStyle = {
    position: 'fixed',
    top: '5%',
    left: '25%',
    width: '50vw',
    maxWidth: '90vw',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    overflowY: 'auto',
};

export default function ModalReporteTrasladosDia(props) {
    const { openModal = false, onClose } = props;

    const { usuario, usuarioData, tipoUsuario, parametrosCajaForm } = useSesion();
    const { handleOpenAlert } = useLayout();
    const { fetchPdfBlob } = useRequest();
    const { listaAlmacenesExhibidoresGeneral, handleObtenerAlmacenesExhibidores } = useInventarios();

    const [internalOpen, setInternalOpen] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [fechaInicio, setFechaInicio] = useState(new Date());
    const [fechaFin, setFechaFin] = useState(new Date());
    const [almacenOrigen, setAlmacenOrigen] = useState("TODOS");
    const [almacenDestino, setAlmacenDestino] = useState("TODOS");
    const [descripcionFiltro, setDescripcionFiltro] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorReporte, setErrorReporte] = useState(false);
    const [pdfUrl, setPdfUrl] = useState("");

    useEffect(() => () => {
        try { if (pdfUrl) URL.revokeObjectURL(pdfUrl); } catch {}
    }, [pdfUrl]);

    useEffect(() => {
        if (openModal) {
            setInternalOpen(true);
            const hoy = new Date();
            setFechaInicio(hoy);
            setFechaFin(hoy);
            setErrorReporte(false);
            try { if (pdfUrl) URL.revokeObjectURL(pdfUrl); } catch {}
            setPdfUrl("");
            if (!listaAlmacenesExhibidoresGeneral || listaAlmacenesExhibidoresGeneral.length === 0) {
                handleObtenerAlmacenesExhibidores("GENERAL");
            }
            setTimeout(() => setModalVisible(true), 50);
        } else {
            setModalVisible(false);
            setTimeout(() => setInternalOpen(false), 300);
        }
    }, [openModal]);

    const getFechaLocal = (date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const opcionesAlmacen = ["TODOS", ...((listaAlmacenesExhibidoresGeneral || []).map((item) =>
        String(item.nombre || item.almacenexhibidor || "").trim().toUpperCase()).filter(Boolean))];

    // Normaliza lo que venga del Autocomplete (string libre u objeto) a
    // texto UPPER. A diferencia de antes, permite cadena vacía mientras se
    // escribe; al enviar, vacío = "TODOS". Así "TODOS/TODOS sin filtro"
    // siempre genera el reporte completo del rango.
    const normalizarAlmacenInput = (valor) => {
        if (valor == null) return "";
        if (typeof valor === "string") return valor.trim().toUpperCase();
        return String(valor.nombre || valor.almacenexhibidor || valor || "").trim().toUpperCase();
    };

    const handleGenerarReporte = async () => {
        if (!fechaInicio || !fechaFin) {
            handleOpenAlert("Seleccione el rango de fechas", "warning");
            return;
        }
        if (fechaFin < fechaInicio) {
            handleOpenAlert("La fecha final no puede ser anterior a la fecha inicial", "warning");
            return;
        }

        setLoading(true);
        setErrorReporte(false);
        try {
            // Vacío = TODOS (sin filtro por almacén).
            const data = {
                fechainicio: getFechaLocal(fechaInicio),
                fechafin: getFechaLocal(fechaFin),
                almacenorigen: (almacenOrigen || "").trim().toUpperCase() || "TODOS",
                almacendestino: (almacenDestino || "").trim().toUpperCase() || "TODOS",
                filtrodescripcion: (descripcionFiltro || "").trim().toUpperCase(),
                sucursal: parametrosCajaForm?.sucursal ?? '',
                pc: (parametrosCajaForm?.caja || "").trim(),
                usuario: usuario || usuarioData?.Usuario || '',
                tipousuario: tipoUsuario || "USUARIO",
            };

            // Detalle técnico solo en consola/log, nunca en pantalla.
            const res = await fetchPdfBlob({
                url: apiReporteTrasladosPeriodo,
                method: "POST",
                body: data,
                loader: "Cargando reporte...",
            });

            if (!res?.ok) {
                console.error("Reporte traslados:", res?.message);
                setErrorReporte(true);
                handleOpenAlert("No se pudo generar el reporte. Verifique los filtros e intente nuevamente.", "error");
                return;
            }

            try { if (pdfUrl) URL.revokeObjectURL(pdfUrl); } catch {}
            const blobPdf = new Blob([res.blob], { type: "application/pdf" });
            if (!blobPdf || blobPdf.size === 0) {
                console.error("Reporte traslados: documento vacío");
                setErrorReporte(true);
                handleOpenAlert("No se pudo generar el reporte. Verifique los filtros e intente nuevamente.", "error");
                return;
            }
            setPdfUrl(URL.createObjectURL(blobPdf));
            handleOpenAlert("Reporte generado exitosamente", "success");
            // El modal permanece abierto para generar de nuevo; se cierra con
            // Cancelar, Escape o click fuera.
        } catch (error) {
            console.error("Reporte traslados:", error?.message);
            setErrorReporte(true);
            handleOpenAlert("No se pudo generar el reporte. Verifique los filtros e intente nuevamente.", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={internalOpen}
            onClose={onClose}
            closeAfterTransition
        >
            <Box sx={{ ...modalStyle, transform: modalVisible ? 'scale(1)' : 'scale(0.8)', opacity: modalVisible ? 1 : 0, transition: 'all 0.3s ease-in-out' }}>
                <Fragment>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Traslados por periodo (Historial del día)
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <Autocomplete
                            freeSolo
                            options={opcionesAlmacen}
                            value={almacenOrigen || "TODOS"}
                            onChange={(_, valor) => setAlmacenOrigen(normalizarAlmacenInput(valor) || "TODOS")}
                            onInputChange={(_, valor, reason) => {
                                if (reason === "clear") { setAlmacenOrigen("TODOS"); return; }
                                setAlmacenOrigen(normalizarAlmacenInput(valor));
                            }}
                            onBlur={() => setAlmacenOrigen((prev) => (prev || "").trim().toUpperCase() || "TODOS")}
                            sx={{ flex: '1 1 200px' }}
                            renderInput={(params) => (
                                <TextField autoComplete="off" {...params} label="Almacén origen" size="small" />
                            )}
                        />
                        <Autocomplete
                            freeSolo
                            options={opcionesAlmacen}
                            value={almacenDestino || "TODOS"}
                            onChange={(_, valor) => setAlmacenDestino(normalizarAlmacenInput(valor) || "TODOS")}
                            onInputChange={(_, valor, reason) => {
                                if (reason === "clear") { setAlmacenDestino("TODOS"); return; }
                                setAlmacenDestino(normalizarAlmacenInput(valor));
                            }}
                            onBlur={() => setAlmacenDestino((prev) => (prev || "").trim().toUpperCase() || "TODOS")}
                            sx={{ flex: '1 1 200px' }}
                            renderInput={(params) => (
                                <TextField autoComplete="off" {...params} label="Almacén destino" size="small" />
                            )}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Filtrar por descripción o clave <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(opcional)</Typography>
                        </Typography>
                        <TextField autoComplete="off"
                            fullWidth
                            size="small"
                            placeholder="Ej: Camisa, Pantalón, clave, etc."
                            value={descripcionFiltro}
                            onChange={(e) => setDescripcionFiltro(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Rango de fechas:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>Fecha Inicio</Typography>
                                <DatePicker
                                    selected={fechaInicio}
                                    onChange={(date) => setFechaInicio(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="custom-datepicker"
                                />
                            </Box>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>Fecha Fin</Typography>
                                <DatePicker
                                    selected={fechaFin}
                                    onChange={(date) => setFechaFin(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="custom-datepicker"
                                    minDate={fechaInicio}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {errorReporte && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            No se pudo generar el reporte. Verifique los filtros e intente nuevamente.
                        </Alert>
                    )}

                    {pdfUrl && (
                        <Box sx={{ mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", height: "50vh" }}>
                            <iframe title="Reporte de traslados" src={pdfUrl} width="100%" height="100%" style={{ border: 0 }} />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                        >
                            {pdfUrl ? "Cerrar" : "Cancelar"}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerarReporte}
                            disabled={loading}
                        >
                            {loading ? "Generando..." : (pdfUrl ? "Regenerar PDF" : "Generar Reporte PDF")}
                        </Button>
                    </Box>
                </Fragment>
            </Box>
        </Modal>
    );
}
